import { useCallback, type KeyboardEvent } from "react";

import { Form } from "antd";

import { RVForm } from "../../../../atoms/RVForm/RVForm";
import { RVFormItem } from "../../../../atoms/RVFormItem/RVFormItem";
import { RVIcon, RVIconSizeOptions } from "../../../../atoms/RVIcon/RVIcon";
import { RVTextarea } from "../../../../atoms/RVTextarea/RVTextarea";
import { RVButton } from "../../../../molecules/RVButton/RVButton";

import styles from "./RVHelpPopupTextareaForm.module.scss";

const PROMPT_FIELD = "prompt";
const DEFAULT_PLACEHOLDER = "Ask me anything…";

export type RVHelpPopupTextareaFormProps = {
    /** Called with the trimmed prompt string when the form is submitted. */
    onSubmit: (prompt: string) => void;
    /** Placeholder text shown inside the textarea. Defaults to "Ask me anything…". */
    placeholder?: string;
};

/**
 * A textarea + send-button form for freeform Copilot prompts inside `RVHelpPopup`.
 * Pressing **Enter** submits the prompt; **Shift+Enter** inserts a newline.
 * The send button is disabled while the textarea is empty or whitespace-only.
 * On submit the trimmed value is forwarded to `onSubmit` and the field is cleared.
 *
 * @example
 * <RVHelpPopupTextareaForm onSubmit={(prompt) => handlePrompt(prompt)} />
 */
export function RVHelpPopupTextareaForm({
    onSubmit,
    placeholder = DEFAULT_PLACEHOLDER,
}: RVHelpPopupTextareaFormProps): JSX.Element {
    const [form] = Form.useForm<{ [PROMPT_FIELD]: string }>();
    const promptValue: string = Form.useWatch(PROMPT_FIELD, form) ?? "";

    const handleFinish = useCallback(
        (values: { [PROMPT_FIELD]: string }) => {
            const trimmed = values[PROMPT_FIELD]?.trim();
            if (!trimmed) {
                return;
            }
            onSubmit(trimmed);
            form.resetFields();
        },
        [form, onSubmit]
    );

    const handleInputEnter = useCallback(
        (e: KeyboardEvent<HTMLTextAreaElement>) => {
            if (e.shiftKey) {
                return;
            }
            e.preventDefault();
            form.submit();
        },
        [form]
    );

    return (
        <RVForm form={form} onFinish={handleFinish} className={styles.promptForm}>
            <div className={styles.inputContainer}>
                <div className={styles.textareaWrap}>
                    <RVFormItem name={PROMPT_FIELD} noStyle>
                        <RVTextarea
                            className={styles.input}
                            placeholder={placeholder}
                            rows={2}
                            onPressEnter={handleInputEnter}
                        />
                    </RVFormItem>
                </div>
                <div className={styles.sendButtonWrap}>
                    <RVButton
                        type="primary"
                        className={styles.submitButton}
                        htmlType="submit"
                        disabled={!promptValue.trim()}
                        loading={false}
                        icon={
                            <RVIcon
                                icon="arrow-right-stem"
                                size={RVIconSizeOptions.BUTTON}
                                className={styles.sendIcon}
                            />
                        }
                        aria-label="Send message"
                    />
                </div>
            </div>
        </RVForm>
    );
}
